import { css, cx } from '@linaria/core'
import { defaultRangeExtractor, type Range, useVirtualizer, type VirtualItem, type Virtualizer } from '@tanstack/react-virtual'
import type React from 'react'
import type { ComponentProps, FC } from 'react'
import { type CSSProperties, memo, type ReactNode, useCallback, useEffect, useMemo, useReducer, useRef } from 'react'
import { style } from '#styles'
import { useIsPrint } from '#utils/useIsPrint'
import { useWindowZoom } from '#utils/useWindowZoom'

const scrollStyles = css`
  position: sticky;
  cursor: pointer;
  top: 0;
  right: 9px;
  width: 40px;
  z-index: 10000;

  appearance: none;
  border: none;
  background-color: transparent;
  margin: 0;
  padding: 8px 5px 8px 0;

  display: flex;
  align-items: stretch;
  justify-content: flex-end;
  transform-origin: center center;
  
  transition: 0.25s ease transform;

  --color: ${style.themeColors.backgroundAccent};
  --hover: ${style.themeColors.backgroundAccent};
  --dot: transparent;

  &.important {
    --color: ${style.themeColors.backgroundAccent};
    --hover: ${style.themeColors.backgroundAccent};
    &.dotted {
      --dot: ${style.themeColors.backgroundDefault};
    }
  }

  &.warning {
    --color: ${style.colors.yellow[500]};
    --hover: ${style.colors.yellow[600]};
    &.dotted {
      --dot: ${style.themeColors.backgroundDefault};
    } 
  }

  &.primary {
    --color: ${style.themeColors.backgroundPrimary};
    --hover: ${style.themeColors.backgroundPrimaryHover};
    &.dotted {
      --dot: ${style.themeColors.backgroundDefault};
    } 
  }

  &.info {
    --color: ${style.themeColors.textDeselected};
    --hover: ${style.themeColors.textDeselected};
    &.dotted {
      --dot: ${style.themeColors.backgroundDefault};
    }
  }

  &.success {
    --color: ${style.colors.green[500]};
    --hover: ${style.colors.green[600]};
    &.dotted {
      --dot: ${style.themeColors.backgroundDefault};
    }
  }

  &.highlight {
    --color: ${style.colors.blue[300]};
    --hover: ${style.colors.blue[500]};
    &.dotted {
      --dot: ${style.themeColors.backgroundDefault};
    }
  }

  > span {
    position: relative;
    width: 16px;
    height: 12px;
    background-color: var(--color);
    border-radius: 24px 0 0 24px;
    transition: 0.25s linear all;
    opacity: 1;
    outline: 1px solid ${style.themeColors.backgroundDefault};
    transform: scale(.5, .5) translate3D(50%, 0, 0);
    will-change: transform;

    @starting-style {
      opacity: 0;
    }

    &::after {
      position: absolute;
      content: " ";
      top: 0;
      left: 0;
      width: 12px;
      height: 12px;
      border-radius: 50px;
      background-color: var(--dot);
      transform: translate(0, 0) scale(.4, .4) ;
      transition: all 250ms;
    }
  }

  

  &:hover {
    > span {
      width: 24px;
      background-color: var(--hover);
      transition: all 250ms;
      transform: scale(1, 1) translate3D(0, 0, 0);
    }
  }
`

const ScrollIndicator = ({ className, kind, dotted, ...props }: ComponentProps<'button'> & { kind?: HighlightType; dotted?: boolean }) => {
  const classNames = cx(className, scrollStyles, kind, dotted && 'dotted')
  return (
    <button className={classNames} {...props}>
      <span />
    </button>
  )
}

const MarkerTrack: FC<{
  data: { id: string }[]
  virtualizer: Virtualizer<HTMLDivElement, Element>
  highlightedEntries: EntryHighlight[]
  headerHeight: number
  list: HTMLDivElement | null
}> = memo(function MarkerTrack({ data, virtualizer, highlightedEntries, headerHeight, list }) {
  return (
    <div className="marker-track">
      {highlightedEntries.map((highlight) => {
        const { index } = highlight
        const { id } = data.at(index) ?? {}
        if (!id) {
          return undefined
        }
        const listHeight = list ? list.clientHeight : 100
        const [startOffset] = virtualizer.getOffsetForIndex(index, 'start') ?? [0]
        const startPosition = (startOffset / virtualizer.getTotalSize()) * listHeight
        // ensure the indicator sticks below the header
        const clampedStartPosition = Math.max(startPosition, headerHeight)
        const style = {
          position: 'absolute',
          transform: `translateY(${clampedStartPosition}px)`,
          zIndex: 'zIndex' in highlight ? highlight.zIndex : 0,
        } satisfies CSSProperties
        if (!('Element' in highlight)) {
          return (
            <ScrollIndicator
              key={id}
              title={highlight.title}
              kind={highlight.type}
              dotted={highlight.dotted}
              style={style}
              onClick={() => {
                virtualizer.scrollToIndex(index, { align: 'center' })
              }}
            />
          )
        }
        return <highlight.Element key={id} style={style} virtualizer={virtualizer} index={index} id={id} />
      })}
    </div>
  )
})

/**
 * A rendering row needs to be a forward referenced, otherwise the virtualizer cannot measure the element size
 * For optimizing the performance, memoize the content of a row and retain
 */
export type ItemGridContentProps<DataEntry, Context> = {
  className?: string
  entry: DataEntry
  index: number
  translate?: number
  style?: React.CSSProperties
  isSticky?: boolean
  context: Context
} & React.RefAttributes<HTMLDivElement>

const virtualizedTableStyle = css`
  position: relative;
  isolation: isolate;
  contain: paint;

  > .marker-track {
    position: absolute;
    right: 0;
    top: 0;
    height: 100%;
    width: 0;
  }

  > .scroll-list {
    isolation: isolate;
    position: relative;
    overflow-y: auto;
    scrollbar-gutter: stable;
    max-height: 100vh;
    height: 100%;
    contain: paint;
    
    @media print {
      max-height: unset;
    }

    scrollbar-color: ${style.colors.silver[200]} color-mix(in srgb, ${style.themeColors.backgroundBoxed} 25%, ${style.themeColors.backgroundDefault});
  }
`

const makeGridRowStyleProps = (offset: number): CSSProperties => {
  return {
    transform: `translateY(${offset}px)`,
  }
}

const useStickyRangeExtractor = (stickyIndices?: number[]) => {
  return useCallback(
    (range: Range) => {
      const sortedIndices = defaultRangeExtractor(range)
      if (!stickyIndices || stickyIndices.every((x) => sortedIndices.includes(x))) {
        return sortedIndices
      }
      for (const index of stickyIndices) {
        if (index < 0) {
          continue
        }
        if (index < range.startIndex) {
          sortedIndices.unshift(index)
        } else if (index > range.endIndex) {
          sortedIndices.push(index)
        }
      }
      return sortedIndices
    },
    [stickyIndices],
  )
}

type HighlightType = 'important' | 'warning' | 'primary' | 'info' | 'success' | 'highlight'
export type EntryHighlight = { index: number } & (
  | { type?: HighlightType; dotted?: boolean; title?: string; zIndex?: number }
  | { Element: FC<{ style: CSSProperties; virtualizer: Virtualizer<HTMLDivElement, Element>; index: number; id: string }> }
)

interface VirtualizedGridProps<Data extends unknown[], Context> {
  className?: string
  gridClassName?: string
  gridStyle?: CSSProperties
  /** A list of indices that should always be rendered, referring to displayData indices */
  keptEntries?: number[]
  /** A list of indices that should be highlighted on the sidebar, referring to displayData indices */
  highlightedEntries?: EntryHighlight[]
  displayData: Data
  estimateSize?: (entry: Data[number]) => number
  headerComponent?: React.ComponentType<{ context: Context }>
  rowComponent: React.ComponentType<ItemGridContentProps<Data[number], Context>>
  footerComponent?: React.ComponentType<{ virtualizer: Virtualizer<HTMLDivElement, Element>; context: Context }>
  headerHeight: number
  context: Context
  setVirtualizerRef: (virtualizer: Virtualizer<HTMLDivElement, Element>) => unknown
  paddingEnd?: number
  testId?: string
}

const virtualizerMark = `virtualized-grid__render-trigger__${crypto.randomUUID()}`
performance.mark(virtualizerMark)

export const VirtualizedGrid = <Data extends { id: string }[], Context>({
  className,
  gridClassName,
  gridStyle,
  keptEntries,
  highlightedEntries,
  displayData,
  estimateSize,
  headerComponent: HeaderComponent,
  rowComponent: RowComponent,
  footerComponent: FooterComponent,
  headerHeight,
  context,
  setVirtualizerRef,
  paddingEnd,
  testId,
}: VirtualizedGridProps<Data, Context>): ReactNode => {
  const isPrint = useIsPrint()

  const rangeExtractor = useStickyRangeExtractor(keptEntries)

  const isFirefox = navigator.userAgent.toLowerCase().includes('firefox')
  // on firefox, the node measurement and offsetting accounts for the zoom setting
  const zoom = useWindowZoom()
  const multiplier = isFirefox ? zoom.inverse : 1.0

  const estimateSizeImpl = useCallback(
    (row: Data[number]) => {
      if (!estimateSize) {
        return 47 * multiplier
      }
      return estimateSize(row)
    },
    [estimateSize, multiplier],
  )

  const emitterDetails = useRef<{
    emitCount: number
    moreWork: boolean
    insertionDirection: 'forward' | 'backward'
    list: HTMLDivElement | null
    virtualizer: Virtualizer<HTMLDivElement, Element> | undefined
  }>({
    emitCount: 2,
    moreWork: true,
    insertionDirection: 'forward',
    list: null,
    virtualizer: undefined,
  })
  const getScrollElement = useCallback(() => emitterDetails.current.list, [])

  const cb = useCallback((idx: number) => displayData[idx].id, [displayData])
  const virtualizer = useVirtualizer({
    count: displayData.length,
    estimateSize: (idx) => estimateSizeImpl(displayData[idx]),
    overscan: 5,
    getScrollElement,
    paddingEnd: isPrint ? 0 : (paddingEnd ?? 300),
    rangeExtractor,
    enabled: !isPrint,
    getItemKey: cb,
  })

  // @ts-expect-error
  if (!virtualizer.options.onChange.__patch) {
    // this small change gives a 2x performance, cool, ey?
    virtualizer.options.onChange = (instance) => {
      if (instance.scrollDirection) {
        emitterDetails.current.insertionDirection = instance.scrollDirection
      }
      emitterDetails.current.moreWork = true
    }
    // @ts-expect-error
    virtualizer.options.onChange.__patch = true
  }

  // monkey patching measureElement, because it is bugged on firefox
  const measureElementCallback = virtualizer.options.measureElement
  virtualizer.options.measureElement = (element, entry, instance) => {
    const result = measureElementCallback(element, entry, instance)
    return result * multiplier
  }
  setVirtualizerRef(virtualizer)
  emitterDetails.current.virtualizer = virtualizer

  const size = virtualizer.getTotalSize()
  const effectiveGridStyle = useMemo(() => {
    return isPrint ? { ...gridStyle } : { ...gridStyle, height: size }
  }, [gridStyle, size, isPrint])

  const rerender = useReducer(() => {
    performance.mark(virtualizerMark)
    return {}
  }, {})[1]

  useEffect(() => {
    const checkReducer = () => {
      if (emitterDetails.current.moreWork) {
        emitterDetails.current.moreWork = false
        rerender()
      }
      timer = requestAnimationFrame(checkReducer)
    }
    let timer = requestAnimationFrame(checkReducer)

    return () => {
      cancelAnimationFrame(timer)
    }
  }, [rerender])

  const virtualizedItems = virtualizer.getVirtualItems()
  const emitted = useRef<VirtualItem[]>([])

  // the entries have changed
  if (virtualizedItems.length !== emitted.current.length || virtualizedItems.some((item, i) => emitted.current[i] !== item)) {
    const nowEmitted: VirtualItem[] = []

    const lastTimeEmitted = [...emitted.current]

    // when backwards scrolling, the appending of nodes also needs to be backwards, otherwise their transition effects occur in the wrong order
    const elements = virtualizedItems
    let newEmitCount = isPrint ? Number.MAX_SAFE_INTEGER : emitterDetails.current.emitCount

    for (let i = 0; i < elements.length; i += 1) {
      const index = emitterDetails.current.insertionDirection === 'forward' ? i : elements.length - 1 - i
      const element = elements[index]

      if (!lastTimeEmitted.some((x) => x.index === element.index)) {
        if (newEmitCount <= 0) {
          continue
        }
        newEmitCount -= 1
      }

      if (emitterDetails.current.insertionDirection === 'forward') {
        nowEmitted.push(element)
      } else {
        nowEmitted.unshift(element)
      }
    }

    emitted.current = nowEmitted
    emitterDetails.current.moreWork = true
  }

  return (
    <div className={cx(className, virtualizedTableStyle)}>
      <div
        className="scroll-list"
        ref={(ref) => {
          emitterDetails.current.list = ref
        }}
      >
        <div className={cx(gridClassName, '_table')} style={effectiveGridStyle} data-testid={testId}>
          {HeaderComponent && <HeaderComponent context={context} />}
          {isPrint &&
            displayData?.map((row, index) => (
              // index + 2 pushes down the first two rows, starting from 2 - 1 is occupied with the header, 0 is invalid
              <RowComponent key={row.id} entry={row} ref={virtualizer.measureElement} index={index + 2} context={context} />
            ))}
          {!isPrint &&
            emitted.current.map((item) => {
              const entry = displayData[item.index]
              const translateOffset = headerHeight + item.start - virtualizer.options.scrollMargin
              const props = makeGridRowStyleProps(translateOffset)
              return (
                <RowComponent
                  key={entry.id}
                  className="_row"
                  ref={virtualizer.measureElement}
                  context={context}
                  translate={translateOffset}
                  entry={entry}
                  index={item.index}
                  style={props}
                />
              )
            })}
          {FooterComponent && <FooterComponent virtualizer={virtualizer} context={context} />}
        </div>
      </div>
      {highlightedEntries && (
        <MarkerTrack
          list={emitterDetails.current.list}
          virtualizer={virtualizer}
          data={displayData}
          highlightedEntries={highlightedEntries}
          headerHeight={headerHeight}
        />
      )}
    </div>
  )
}
